import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Button, Field, Notice } from '../../components/ui';
import { PasswordStrength } from '../../components/PasswordStrength';
import { SocialButtons } from '../../components/SocialButtons';
import { authErrorMessage, confirmSignUp, passwordChecks, resendCode, signIn, signUp } from '../../lib/auth';
import { clearPending, readPending, registerSchool, savePending } from '../../lib/register';
import { useSession } from '../../lib/session';
import { clearPendingInvite, getInvite, registerWithInvite, ROLE_WORD, savePendingInvite } from '../../lib/invites';
import { CONTACT_URL, useSelfServe } from '../../lib/signup';
import { config } from '../../config';

/**
 * Set up a school: your details and the school's name, then the 6-digit code
 * we email to prove the address, then straight into setup. Teachers joining a
 * school use the app with their school code instead.
 *
 * With ?invite=<token> (from an invite email) it joins the invited school
 * instead: the email is fixed to the invited one and there's no school name.
 */
export function SignUpPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const selfServe = useSelfServe();
  const pending = readPending();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite') ?? pending?.inviteToken ?? null;
  const inviteQuery = useQuery({ queryKey: ['invite', inviteToken], queryFn: () => getInvite(inviteToken!), enabled: !!inviteToken, retry: false });
  const invite = inviteQuery.data?.status === 'open' ? inviteQuery.data : null;
  const [name, setName] = useState(pending?.name ?? '');
  const [email, setEmail] = useState(pending?.email ?? '');
  const [schoolName, setSchoolName] = useState(pending?.schoolName ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'details' | 'code'>(pending ? 'code' : 'details');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invite || !inviteToken) return;
    setEmail(invite.email);
    // Kept for this tab so Google or Apple sign-up joins this school too.
    savePendingInvite(inviteToken);
  }, [invite, inviteToken]);

  // Which path: set up an organisation, or (staff) join a team in the app (O-02).
  const [path, setPath] = useState<'choose' | 'organisation' | 'join'>(pending || inviteToken ? 'organisation' : 'choose');
  // Field errors show once a field has been left, or after Continue (O-01).
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const touch = (k: string) => setTouched((t) => ({ ...t, [k]: true }));
  const fieldErrors: Record<string, string | null> = {
    name: name.trim() ? null : 'Enter your name.',
    org: invite || schoolName.trim() ? null : 'Enter your organisation’s name.',
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? null : email.trim() ? 'Enter a full email address, like name@school.org.' : 'Enter your work email.',
    password: passwordChecks(password).every((c) => c.ok) ? null : password ? 'Your password needs every rule below.' : 'Choose a password.',
  };
  const shown = (k: string) => ((touched[k] || submitted) ? fieldErrors[k] : null);
  const errorList = (['name', 'org', 'email', 'password'] as const).filter((k) => fieldErrors[k]);
  const detailsValid = errorList.length === 0;

  const submitDetails = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitted(true);
    if (!detailsValid) {
      // The summary below links to each field; move focus to it for screen readers.
      requestAnimationFrame(() => document.getElementById('signup-errors')?.focus());
      return;
    }
    setBusy(true);
    try {
      await signUp(email, password, name);
      savePending({ email, name, schoolName, inviteToken: invite ? inviteToken ?? undefined : undefined });
      setStep('code');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmSignUp(email, code);
      if (!password) {
        // Page was reloaded after the code was sent: the password isn't kept, so sign in normally.
        clearPending();
        navigate('/sign-in', { replace: true, state: { verified: true } });
        return;
      }
      const step = await signIn(email, password, false);
      if (step.kind !== 'done') throw new Error('sign-in-step');
      if (invite && inviteToken) {
        await registerWithInvite(email, name, inviteToken);
        clearPending();
        clearPendingInvite();
        await refresh();
        navigate('/', { replace: true });
        return;
      }
      await registerSchool(email, name, schoolName);
      clearPending();
      await refresh();
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'code') {
    return (
      <AuthLayout title="Check your email" subtitle={`We sent a 6-digit code to ${email}. It proves the address is yours.`}>
        <form className="auth-form" onSubmit={submitCode} noValidate>
          {error ? <Notice tone="error">{error}</Notice> : null}
          {info ? <Notice tone="success">{info}</Notice> : null}
          <Field id="signup-code" label="Code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          <Button type="submit" size="lg" block loading={busy}>
            Confirm and continue
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={async () => {
              setError(null);
              try {
                await resendCode(email);
                setInfo('We sent a new code.');
              } catch (err) {
                setError(authErrorMessage(err));
              }
            }}
          >
            Send a new code
          </Button>
        </form>
        <p className="auth-foot">
          Wrong email?{' '}
          <button
            className="link-button"
            onClick={() => {
              clearPending();
              setStep('details');
            }}
          >
            Start again
          </button>
        </p>
      </AuthLayout>
    );
  }

  if (path === 'choose') {
    return (
      <AuthLayout title="Get started with EducAitor" subtitle="Tell us what you&rsquo;d like to do.">
        <div className="path-cards">
          {selfServe ? (
            <button type="button" className="path-card" onClick={() => setPath('organisation')}>
              <strong>Set up my organisation</strong>
              <span className="muted">For leaders. Create your organisation&rsquo;s account, then invite your staff.</span>
            </button>
          ) : (
            <a className="path-card" href={CONTACT_URL} target="_blank" rel="noreferrer">
              <strong>Set up my organisation</strong>
              <span className="muted">For leaders. Get in touch and we&rsquo;ll set up your organisation&rsquo;s account with you.</span>
            </a>
          )}
          <button type="button" className="path-card" onClick={() => setPath('join')}>
            <strong>Join my team (I have a code)</strong>
            <span className="muted">For staff whose organisation already uses EducAitor.</span>
          </button>
          <a className="path-card" href="https://educaitor.ai/contact-us/" target="_blank" rel="noreferrer">
            <strong>Just me</strong>
            <span className="muted">Using EducAitor on your own? Get in touch and we&rsquo;ll set you up.</span>
          </a>
        </div>
        <p className="auth-foot">
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  if (path === 'join') {
    return (
      <AuthLayout title="Join your team in the app" subtitle="Staff join from the EducAitor app on their phone.">
        <ol className="join-steps">
          <li>
            Download EducAitor from{' '}
            {config.appStoreUrl ? (
              <a href={config.appStoreUrl} target="_blank" rel="noreferrer">
                the App Store
              </a>
            ) : (
              'the App Store'
            )}
            {config.playStoreUrl ? (
              <>
                {' or '}
                <a href={config.playStoreUrl} target="_blank" rel="noreferrer">
                  Google Play
                </a>
              </>
            ) : null}
            .{config.playStoreUrl ? null : ' Android is coming soon.'}
          </li>
          <li>Open it and tap &ldquo;Join your organisation&rdquo;.</li>
          <li>Enter the organisation code your leader gave you. They&rsquo;ll approve you, then you&rsquo;re in.</li>
        </ol>
        <Notice>Got an invite email? Tap the button in it on your phone instead. You won&rsquo;t need a code.</Notice>
        <Button variant="ghost" onClick={() => setPath('choose')}>
          Back
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={invite ? (invite.personal ? 'Create your EducAitor account' : invite.makeOwner ? `Set up ${invite.schoolName}` : `Join ${invite.schoolName}`) : 'Set up your organisation'}
      subtitle={
        invite
          ? invite.makeOwner
            ? `You'll be its account owner. Create your account, then the short setup.`
            : `${invite.inviterName ?? 'Your organisation'} invited you as ${ROLE_WORD[invite.role]}. Create your account to join.`
          : 'Create your organisation’s account, then invite your staff.'
      }
    >
      <SocialButtons />
      <form className="auth-form" onSubmit={submitDetails} noValidate>
        {error ? <Notice tone="error">{error}</Notice> : null}
        {submitted && errorList.length ? (
          <div id="signup-errors" className="error-summary" tabIndex={-1} role="alert">
            <p>Check {errorList.length === 1 ? 'this' : 'these'} before continuing:</p>
            <ul>
              {errorList.map((k) => (
                <li key={k}>
                  <a href={`#signup-${k}`}>{fieldErrors[k]}</a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {inviteToken && inviteQuery.data && !invite ? (
          <Notice tone="error">This invite can&rsquo;t be used any more. Ask your organisation to send a new one.</Notice>
        ) : null}
        <div className={invite ? undefined : 'auth-row-2'}>
          <Field
            id="signup-name"
            label="Your name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => touch('name')}
            error={shown('name')}
            autoFocus
          />
          {invite ? null : (
            <Field
              id="signup-org"
              label="Organisation name"
              autoComplete="organization"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              onBlur={() => touch('org')}
              error={shown('org')}
            />
          )}
        </div>
        <Field
          id="signup-email"
          label="Work email"
          type="email"
          autoComplete="email"
          value={email}
          readOnly={!!invite}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => touch('email')}
          error={shown('email')}
        />
        <Field
          id="signup-password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => touch('password')}
          error={shown('password')}
          trailing={
            <Button type="button" variant="ghost" onClick={() => setShowPassword((v) => !v)} aria-pressed={showPassword}>
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          }
        />
        <PasswordStrength password={password} id="signup-password-rules" />
        <Button type="submit" size="lg" block loading={busy}>
          Continue
        </Button>
      </form>
      <p className="auth-foot">
        Already have an account? <Link to="/sign-in">Sign in</Link>
        <br />
        {invite ? (
          <span className="faint">Staff join from the EducAitor app with their organisation code.</span>
        ) : (
          <button type="button" className="link-button" onClick={() => setPath('choose')}>
            Joining a team instead? See other options
          </button>
        )}
      </p>
    </AuthLayout>
  );
}
